import type { CharacterKind } from '@/components'
import type { PlayerInput } from '@/input/InputManager'
import {
  PROTOCOL_VERSION,
  packInput,
  type ClientMessage,
  type ErrorCode,
  type RoomConfig,
  type RoomView,
  type ServerMessage,
  type SnapshotMessage,
} from './protocol'

/** The part of a WebSocket this client uses; the browser's and `ws` both fit. */
export interface SocketLike {
  readonly readyState: number
  send(data: string): void
  close(code?: number, reason?: string): void
  onopen: ((event: unknown) => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  onclose: ((event: unknown) => void) | null
  onerror: ((event: unknown) => void) | null
}

export interface NetClientOptions {
  url: string
  nick: string
  /** From a previous connection: asks the server for the same slot back. */
  token?: string | null
  /** How to open the socket; the browser's WebSocket by default. */
  open?: (url: string) => SocketLike
}

interface Handlers {
  room: (view: RoomView) => void
  /** `bytes` is the length of the message as received, before parsing. */
  snapshot: (snap: SnapshotMessage, bytes: number) => void
  error: (code: ErrorCode, message?: string) => void
  close: () => void
}

const OPEN = 1

/**
 * One connection to the game server.
 *
 * Knows the protocol and nothing about the page: no DOM, no renderer, so the
 * same code runs in the browser and in the headless network harness.
 */
export class NetClient {
  token: string | null
  room: RoomView | null = null
  /** Round trip of the last ping, in milliseconds. */
  rtt = 0

  private socket: SocketLike | null = null
  private seq = 0
  private readonly handlers: { [K in keyof Handlers]: Array<Handlers[K]> } = {
    room: [],
    snapshot: [],
    error: [],
    close: [],
  }

  constructor(private readonly options: NetClientOptions) {
    this.token = options.token ?? null
  }

  get connected(): boolean {
    return this.socket !== null && this.socket.readyState === OPEN
  }

  on<K extends keyof Handlers>(event: K, handler: Handlers[K]): void {
    this.handlers[event].push(handler)
  }

  /** Opens the socket and says hello; resolves on the server's welcome. */
  connect(): Promise<void> {
    const open = this.options.open ?? ((url: string) => new WebSocket(url) as SocketLike)
    const socket = open(this.options.url)
    this.socket = socket

    return new Promise((resolve, reject) => {
      let welcomed = false
      socket.onopen = () => {
        this.send({
          t: 'hello',
          v: PROTOCOL_VERSION,
          nick: this.options.nick,
          ...(this.token ? { token: this.token } : {}),
        })
      }
      socket.onmessage = (event) => {
        const text = String(event.data)
        const message = JSON.parse(text) as ServerMessage
        if (message.t === 'welcome') {
          this.token = message.token
          welcomed = true
          resolve()
          return
        }
        if (message.t === 'error' && !welcomed) reject(new Error(message.code))
        this.receive(message, text.length)
      }
      socket.onclose = () => {
        if (this.socket === socket) this.socket = null
        if (!welcomed) reject(new Error('closed'))
        for (const handler of this.handlers.close) handler()
      }
      socket.onerror = () => {
        if (!welcomed) reject(new Error('socket error'))
      }
    })
  }

  create(config: RoomConfig): void {
    this.send({ t: 'create', config })
  }

  join(code: string): void {
    this.send({ t: 'join', code })
  }

  rename(nick: string): void {
    this.send({ t: 'nick', nick })
  }

  leave(): void {
    this.room = null
    this.send({ t: 'leave' })
  }

  pick(model: CharacterKind): void {
    this.send({ t: 'pick', model })
  }

  watch(on: boolean): void {
    this.send({ t: 'watch', on })
  }

  configure(config: RoomConfig): void {
    this.send({ t: 'config', config })
  }

  start(): void {
    this.send({ t: 'start' })
  }

  abort(): void {
    this.send({ t: 'abort' })
  }

  /** Sends one slot's input; returns its sequence number. */
  sendInput(input: PlayerInput): number {
    this.seq++
    this.send({ t: 'in', ...packInput(input, this.seq) })
    return this.seq
  }

  ping(now = performance.now()): void {
    this.send({ t: 'ping', c: now })
  }

  close(): void {
    this.socket?.close()
    this.socket = null
  }

  private send(message: ClientMessage): void {
    if (this.connected) this.socket!.send(JSON.stringify(message))
  }

  private receive(message: ServerMessage, bytes: number): void {
    switch (message.t) {
      case 'room': {
        const { t: _, ...view } = message
        this.room = view
        for (const handler of this.handlers.room) handler(view)
        break
      }
      case 'snap':
        for (const handler of this.handlers.snapshot) handler(message, bytes)
        break
      case 'pong':
        this.rtt = performance.now() - message.c
        break
      case 'error':
        for (const handler of this.handlers.error) handler(message.code, message.message)
        break
      case 'welcome':
        break
    }
  }
}
